import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for Supabase realtime subscriptions
 * @param {string} table - Table name to subscribe to
 * @param {string} userId - User ID for filtering
 * @param {function} onInsert - Callback for INSERT events
 * @param {function} onUpdate - Callback for UPDATE events
 * @param {function} onDelete - Callback for DELETE events
 * @param {object} options - Additional options (filter, event types)
 */
export const useRealtimeSubscription = ({
  table,
  userId,
  onInsert,
  onUpdate,
  onDelete,
  options = {}
}) => {
  const [status, setStatus] = useState('disconnected');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!table || !userId) {
      setStatus('idle');
      return;
    }

    setStatus('connecting');
    setError(null);

    // Create channel for realtime subscription
    const channel = supabase?.channel(`${table}_changes_${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          try {
            switch (payload?.eventType) {
              case 'INSERT':
                onInsert?.(payload?.new);
                break;
              case 'UPDATE':
                onUpdate?.(payload?.new, payload?.old);
                break;
              case 'DELETE':
                onDelete?.(payload?.old);
                break;
              default:
                break;
            }
          } catch (err) {
            console.error(`Realtime ${payload?.eventType} error:`, err);
            setError(err);
          }
        }
      )?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setStatus('connected');
        } else if (status === 'CLOSED') {
          setStatus('disconnected');
        } else if (status === 'CHANNEL_ERROR') {
          setStatus('error');
          setError(new Error('Channel subscription error'));
        }
      });

    // Cleanup subscription on unmount
    return () => {
      supabase?.removeChannel(channel);
      setStatus('disconnected');
    };
  }, [table, userId, onInsert, onUpdate, onDelete]);

  return { status, error };
};

// Specialized hooks for each table

export const useTransactionsRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'transactions',
    userId,
    ...callbacks
  });
};

export const useBudgetCategoriesRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'budget_categories',
    userId,
    ...callbacks
  });
};

export const useFinancialGoalsRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'financial_goals',
    userId,
    ...callbacks
  });
};

export const useInvestmentsRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'investments',
    userId,
    ...callbacks
  });
};

export const useFinancialSystemsRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'financial_systems',
    userId,
    ...callbacks
  });
};

export const useNotificationsRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'notifications',
    userId,
    ...callbacks
  });
};

export const useRecurringBillsRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'recurring_bills',
    userId,
    ...callbacks
  });
};

export const useRetirementPlansRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'retirement_plans',
    userId,
    ...callbacks
  });
};

export const useRetirementScenariosRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'retirement_scenarios',
    userId,
    ...callbacks
  });
};

export const useSavedReportsRealtime = (userId, callbacks) => {
  return useRealtimeSubscription({
    table: 'saved_reports',
    userId,
    ...callbacks
  });
};
